import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@travellegacy/nexops-design-system';
import {
  ArrowLeft,
  Clock,
  ExternalLink,
  RotateCcw,
  ShieldAlert,
  ShieldCheck,
  Timer,
  User,
} from 'lucide-react';
import { useDemandDetail, useSLAs } from '../hooks/useDemands';
import { formatDurationRich, formatDurationClock } from '../utils/format-duration';
import { buildNotionPageUrl } from '../utils/notion-url';

/**
 * Página de detalhe de uma demanda, com timeline de ciclos, KPIs específicos
 * (nº de ciclos, tempo total, tempo médio, indicador de rework) e link para o
 * Notion. Rota: `/demands/:id` (protegida por `CLIENT_OPERATIONS` no App.tsx).
 */
export function DemandDetailPage() {
  const params = useParams<{ id: string }>();
  const demandId = params.id;
  const query = useDemandDetail(demandId);

  const demand = query.data?.data;

  // Cronômetro em tempo real para o ciclo aberto (se houver).
  const [nowMs, setNowMs] = useState(() => Date.now());
  const openCycle = useMemo(
    () => demand?.cycles.find((c) => !c.endedAt) ?? null,
    [demand?.cycles]
  );
  useEffect(() => {
    if (!openCycle) return;
    const interval = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [openCycle?.id]);

  const notionUrl = buildNotionPageUrl(demand?.notionPageId ?? null);

  const averageCycleMs = useMemo(() => {
    if (!demand) return 0;
    const closed = demand.cycles.filter((c) => c.durationMs !== null);
    if (closed.length === 0) return 0;
    const total = closed.reduce((sum, c) => sum + (c.durationMs ?? 0), 0);
    return total / closed.length;
  }, [demand]);

  const hasRework = !!demand && demand.cyclesCount > 1;

  // Busca SLA do tipo, se houver (usado no header + timeline).
  const slasQuery = useSLAs();
  const typeSla = useMemo(() => {
    if (!demand?.type) return null;
    const found = slasQuery.data?.data.find((s) => s.type === demand.type);
    return found && found.enabled ? found : null;
  }, [demand?.type, slasQuery.data]);

  if (query.isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-6 w-40 animate-pulse rounded bg-muted" />
        <div className="h-64 w-full animate-pulse rounded bg-muted/50" />
      </div>
    );
  }

  if (!demand) {
    return (
      <div className="space-y-4">
        <Link
          to="/demands"
          className="inline-flex items-center gap-2 rounded-md px-2 py-1 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar para demandas
        </Link>
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            Demanda não encontrada.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-2">
          <Link
            to="/demands"
            className="-ml-2 inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Demandas
          </Link>
          <h1 className="flex flex-wrap items-center gap-2 text-2xl font-bold text-foreground">
            {demand.title}
            {demand.type && (
              <Badge variant="info" className="font-normal">
                {demand.type}
              </Badge>
            )}
            {hasRework && (
              <Badge variant="warning" className="font-normal">
                <RotateCcw className="mr-1 h-3 w-3" />
                Retrabalho
              </Badge>
            )}
            {typeSla && (
              <Badge variant="outline" className="font-normal">
                <ShieldCheck className="mr-1 h-3 w-3" />
                SLA: {Math.round(typeSla.targetMs / 3_600_000)}h
              </Badge>
            )}
          </h1>
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <User className="h-3.5 w-3.5" />
            {demand.responsibleName}
            <span aria-hidden="true">·</span>
            <span>
              Status: <span className="font-medium text-foreground">{demand.currentStatus}</span>
            </span>
          </p>
        </div>

        {notionUrl && (
          <a
            href={notionUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-9 items-center gap-2 rounded-md border border-input bg-background px-3 text-sm font-medium shadow-sm transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ExternalLink className="h-4 w-4" />
            Abrir no Notion
          </a>
        )}
      </div>

      {/* KPIs específicos da demanda */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <MiniKpi label="Ciclos" value={demand.cyclesCount.toString()} />
        <MiniKpi label="Tempo total" value={formatDurationRich(demand.totalActiveDurationMs)} />
        <MiniKpi
          label="Tempo médio por ciclo"
          value={averageCycleMs > 0 ? formatDurationRich(averageCycleMs) : '—'}
        />
        <MiniKpi
          label="Status"
          value={demand.isActive ? 'Em andamento' : 'Fora de andamento'}
          valueClassName={demand.isActive ? 'text-emerald-600 dark:text-emerald-400' : undefined}
        />
      </div>

      {/* Timeline de ciclos */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Histórico de ciclos
          </CardTitle>
        </CardHeader>
        <CardContent>
          {demand.cycles.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Ainda não há ciclos registrados para essa demanda.
            </p>
          ) : (
            <ol className="relative space-y-4 border-l border-border pl-6">
              {demand.cycles.map((c, idx) => {
                const isOpen = !c.endedAt;
                const elapsed = isOpen
                  ? Math.max(0, nowMs - new Date(c.startedAt).getTime())
                  : c.durationMs ?? 0;

                // SLA por ciclo: se há SLA no tipo, verifica se elapsed
                // (duração real do ciclo fechado, ou tempo corrido do aberto)
                // supera targetMs.
                const slaBreach = typeSla ? elapsed > typeSla.targetMs : false;
                const slaWithin =
                  typeSla && !isOpen ? elapsed <= typeSla.targetMs : false;
                return (
                  <li key={c.id} className="relative">
                    <span
                      className={`absolute -left-[27px] mt-1 flex h-3 w-3 rounded-full ring-4 ring-background ${
                        isOpen ? 'bg-emerald-500 animate-pulse' : 'bg-muted-foreground'
                      }`}
                      aria-hidden="true"
                    />
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">
                          Ciclo #{demand.cycles.length - idx}
                          {isOpen && (
                            <Badge variant="success" className="ml-2 font-normal">
                              Aberto
                            </Badge>
                          )}
                          {slaBreach && (
                            <Badge
                              variant="error"
                              className="ml-2 font-normal inline-flex items-center gap-1"
                            >
                              <ShieldAlert className="h-3 w-3" />
                              Fora do SLA
                            </Badge>
                          )}
                          {slaWithin && (
                            <Badge
                              variant="success"
                              className="ml-2 font-normal inline-flex items-center gap-1"
                            >
                              <ShieldCheck className="h-3 w-3" />
                              Dentro do SLA
                            </Badge>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {c.responsibleName} · Iniciado em{' '}
                          {new Date(c.startedAt).toLocaleString('pt-BR')}
                          {c.endedAt && (
                            <>
                              {' '}
                              · Encerrado em {new Date(c.endedAt).toLocaleString('pt-BR')}
                            </>
                          )}
                        </p>
                        {(c.startedFromStatus || c.endedToStatus) && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {c.startedFromStatus && (
                              <>
                                de{' '}
                                <span className="font-medium">{c.startedFromStatus}</span>
                              </>
                            )}
                            {c.startedFromStatus && c.endedToStatus && ' → '}
                            {c.endedToStatus && (
                              <>
                                para{' '}
                                <span className="font-medium">{c.endedToStatus}</span>
                              </>
                            )}
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-2 font-mono text-sm tabular-nums">
                        <Timer className="h-4 w-4 text-muted-foreground" />
                        {isOpen ? formatDurationClock(elapsed) : formatDurationRich(elapsed)}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface MiniKpiProps {
  label: string;
  value: string;
  valueClassName?: string;
}

function MiniKpi({ label, value, valueClassName }: MiniKpiProps) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div className={`mt-1 text-xl font-semibold tabular-nums ${valueClassName ?? ''}`}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

export default DemandDetailPage;
